import React, { useEffect, useRef, useState } from "react";
import {
  FaArrowCircleLeft,
  FaArrowCircleRight,
  FaCog,
  FaBriefcaseMedical,
  FaFileInvoice,
  FaShieldAlt,
  FaCalendarCheck,
  FaWhatsapp,
  FaSave,
  FaCapsules,
  FaChartLine,
} from "react-icons/fa";
import { FaPrescriptionBottle } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import GeneralSettings from "../../components/his/settings/GeneralSettings";
import useFetchData from "../../hooks/UseFetchData";
import { fetchData } from "../../helpers/fetchData";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  updateZatcaSettings,
  updateInvoiceSettings,
  updateInsuranceApprovalSettings,
  updateVisitSettings,
  updateClaimsSettings,
  updateMedicalReportSettings,
  updatePrescriptionSettings,
  updateDiscountSettings,
  updatePayBillSettings,
  updateGlPostingSettings,
} from "../../redux/slices/AuthSlice";
import { DEFAULT_INSURANCE_APPROVAL_SETTINGS } from "../../utils/insuranceApprovalSettings";
import { MedicalReportSettings } from "./MedicalReportSettings";
import {
  PrescriptionSettings,
  DEFAULT_PRESCRIPTION_SETTINGS,
} from "./PrescriptionSettings";
import CheckPermission from "../../hooks/CheckPermission";

function Settings() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission } = CheckPermission();
  const [updateLoading, setUpdateLoading] = useState(false);
  const { zatcaSettings } = useSelector((state) => state.auth);
  const { loading: settingLoading, data, executeFetch } = useFetchData();

  const [settings, setSettings] = useState({
    invoice_size: {
      value: "a4_receipt",
      privcy_policy: false,
      is_arabic: false,
      is_zatca: false,
      mobile_otp: false,
      allow_update_after_erp_posting: false,
      zatca_settings: {
        enable_send_cash_invoices: false,
        enable_send_insurance_invoices: false,
        enable_send_corporate_invoices: false,
        enable_send_government_invoices: false,
        enable_send_deposit_invoices: false,
        enable_send_withdraw_invoices: false,
        is_send_patient_amount: false,
      },
      insurance_approval_settings: DEFAULT_INSURANCE_APPROVAL_SETTINGS,
      medical_report_settings: {
        template: "standard_medical_report",
        show_business_logo: true,
        show_chai_logo: false,
        show_vision_2030_logo: false,
        chai_logo_url: "",
        vision_2030_logo_url: "",
      },
      prescription_settings: { ...DEFAULT_PRESCRIPTION_SETTINGS },
      visit_settings: {
        separate_walkin_and_slots: false,
      },
      paybill_settings: { display_mode: "category_tabs" },
      whatsapp: {
        status: false,
        module_name: null,
      },
    },
    gl_posting_settings: {
      name: "gl_posting_settings",
      value: "gl_posting_settings",
      gl_posting_settings: { revenue_grouping_mode: "department" },
    },
  });

  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("general");
  const [generalSectionIndex, setGeneralSectionIndex] = useState(0);
  const generalSections = [
    { label: "Claims Settings", icon: FaSave },
    { label: "Invoice Settings", icon: FaFileInvoice },
    { label: "Insurance Approval Settings", icon: FaShieldAlt },
    { label: "Visit Settings", icon: FaCalendarCheck },
    { label: "WhatsApp Settings", icon: FaWhatsapp },
    { label: "Pharmacy Settings", icon: FaPrescriptionBottle },
    { label: "Medical Report Settings", icon: FaBriefcaseMedical },
    { label: "Discount Settings", icon: FaSave },
    { label: "PayBill Settings", icon: FaFileInvoice },
    { label: "GL Posting Settings", icon: FaChartLine },
  ];

  const ZATCA_DEFAULTS = {
    enable_send_cash_invoices: false,
    enable_send_insurance_invoices: false,
    enable_send_corporate_invoices: false,
    enable_send_government_invoices: false,
    enable_send_deposit_invoices: false,
    enable_send_withdraw_invoices: false,
    is_send_patient_amount: false,
  };

  const normalizeSetting = (setting) => {
    if (setting.name === "claims_settings") {
      return {
        ...setting,
        claims_settings: {
          include_zero_price_items: false,
          max_per_claim_limit: false,
          ...(setting.claims_settings || {}),
        },
      };
    }
    if (setting.name === "gl_posting_settings") {
      return {
        ...setting,
        gl_posting_settings: {
          revenue_grouping_mode: "department",
          ...(setting.gl_posting_settings || {}),
        },
      };
    }
    if (setting.name === "discount_settings") {
      const discountSettings = setting.discount_settings || {};
      return {
        ...setting,
        discount_settings: {
          ...discountSettings,
          default_limit:
            Number(discountSettings.default_limit) > 0
              ? Number(discountSettings.default_limit)
              : 100,
          department_limits: discountSettings.department_limits || [],
        },
      };
    }
    if (setting.name !== "invoice_size" && setting.name !== "invoice_settings")
      return setting;
    return {
      ...setting,
      name: "invoice_size",
      zatca_settings: {
        ...ZATCA_DEFAULTS,
        ...(setting.zatca_settings || {}),
      },
      insurance_approval_settings: {
        ...DEFAULT_INSURANCE_APPROVAL_SETTINGS,
        ...(setting.insurance_approval_settings || {}),
      },
      visit_settings: {
        separate_walkin_and_slots: false,
        ...(setting.visit_settings || {}),
      },
      prescription_settings: {
        ...DEFAULT_PRESCRIPTION_SETTINGS,
        ...(setting.prescription_settings || {}),
      },
      paybill_settings: {
        display_mode: setting.paybill_settings?.display_mode || "category_tabs",
      },
    };
  };

  const fetchSettings = async () => {
    const res = await executeFetch("POST", "/v3/settings/list");
    if (res?.data?.length) {
      const initialSettings = {};
      res.data.forEach((setting) => {
        initialSettings[setting.name] = normalizeSetting(setting);
      });
      setSettings(initialSettings);
    } else {
      const defaultRes = await executeFetch(
        "POST",
        "/v3/settings/importDefault",
      );
      if (defaultRes?.data?.length) {
        const defaultSettings = {};
        defaultRes.data.forEach((setting) => {
          defaultSettings[setting.name] = normalizeSetting(setting);
        });
        setSettings(defaultSettings);
      }
    }
  };

  const handleSettingsChange = (event) => {
    const { name, value } = event.target;
    setLoading(true);
    try {
      setSettings((prev) => ({
        ...prev,
        [name]: {
          ...prev[name],
          value,
        },
      }));
    } catch (err) {
      console.error("Error updating settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (setting) => {
    setUpdateLoading(true);
    try {
      if (!setting?.id || !setting?.name) {
        toast.error("Invalid setting object - missing id or name");
        return;
      }

      const payload = {
        id: setting.id,
        name: setting.name,
        is_zatca: setting.is_zatca,
        is_arabic: setting.is_arabic,
        value: setting.value || "a4_receipt",
        privcy_policy: setting.privcy_policy,
        allow_update_after_erp_posting: setting.allow_update_after_erp_posting,
        zatca_settings: {
          enable_send_cash_invoices:
            setting.zatca_settings?.enable_send_cash_invoices || false,
          enable_send_insurance_invoices:
            setting.zatca_settings?.enable_send_insurance_invoices || false,
          enable_send_corporate_invoices:
            setting.zatca_settings?.enable_send_corporate_invoices || false,
          enable_send_government_invoices:
            setting.zatca_settings?.enable_send_government_invoices || false,
          enable_send_deposit_invoices:
            setting.zatca_settings?.enable_send_deposit_invoices || false,
          enable_send_withdraw_invoices:
            setting.zatca_settings?.enable_send_withdraw_invoices || false,
          enable_edit_zatca_posted_invoice:
            setting.zatca_settings?.enable_edit_zatca_posted_invoice || false,
          is_send_patient_amount:
            setting.zatca_settings?.is_send_patient_amount || false,
        },
        insurance_approval_settings: {
          ...DEFAULT_INSURANCE_APPROVAL_SETTINGS,
          ...(setting.insurance_approval_settings || {}),
        },
        medical_report_settings: {
          template:
            setting.medical_report_settings?.template ||
            "standard_medical_report",
          show_business_logo:
            setting.medical_report_settings?.show_business_logo ?? true,
          show_chai_logo:
            setting.medical_report_settings?.show_chai_logo || false,
          show_vision_2030_logo:
            setting.medical_report_settings?.show_vision_2030_logo || false,
          chai_logo_url: setting.medical_report_settings?.chai_logo_url || "",
          vision_2030_logo_url:
            setting.medical_report_settings?.vision_2030_logo_url || "",
        },
        prescription_settings: {
          items: setting.prescription_settings?.items ?? true,
          diagnosis: setting.prescription_settings?.diagnosis ?? true,
          vital_signs: setting.prescription_settings?.vital_signs ?? true,
        },
        claims_settings:
          setting.name === "claims_settings"
            ? {
                include_zero_price_items:
                  setting.claims_settings?.include_zero_price_items || false,
                max_per_claim_limit:
                  setting.claims_settings?.max_per_claim_limit || false,
              }
            : undefined,
        discount_settings:
          setting.name === "discount_settings"
            ? {
                default_limit: Number(
                  setting.discount_settings?.default_limit || 100,
                ),
                department_limits: (
                  setting.discount_settings?.department_limits || []
                ).map((item) => ({
                  ...item,
                  limit: Number(item.limit || 100),
                })),
              }
            : undefined,
        visit_settings: {
          separate_walkin_and_slots:
            setting.visit_settings?.separate_walkin_and_slots || false,
        },
        paybill_settings: {
          display_mode:
            setting.paybill_settings?.display_mode || "category_tabs",
        },
        gl_posting_settings:
          setting.name === "gl_posting_settings"
            ? {
                revenue_grouping_mode:
                  setting.gl_posting_settings?.revenue_grouping_mode ||
                  "department",
              }
            : undefined,
        whatsapp: {
          status: setting.whatsapp?.status || false,
          module_name: setting.whatsapp?.module_name || null,
          mobile_otp: setting.whatsapp?.module_name?.includes("otp")
            ? true
            : false,
        },
      };

      const res = await fetchData("POST", "/v3/settings/update", payload);

      if (res) {
        toast.success(res?.message);
        if (res?.data?.zatca_settings || res?.data?.is_zatca !== undefined) {
          dispatch(
            updateZatcaSettings({
              is_zatca: res?.data?.is_zatca ?? false,
              ...res?.data?.zatca_settings,
            }),
          );
        }
        if (res?.data?.allow_update_after_erp_posting !== undefined) {
          dispatch(
            updateInvoiceSettings({
              allow_update_after_erp_posting:
                res?.data?.allow_update_after_erp_posting ?? false,
            }),
          );
        }
        if (res?.data?.insurance_approval_settings) {
          dispatch(
            updateInsuranceApprovalSettings(
              res.data.insurance_approval_settings,
            ),
          );
        }
        if (res?.data?.visit_settings) {
          dispatch(updateVisitSettings(res.data.visit_settings));
        }
        if (
          res?.data?.name === "claims_settings" &&
          res?.data?.claims_settings
        ) {
          dispatch(updateClaimsSettings(res.data.claims_settings));
        }
        if (res?.data?.medical_report_settings) {
          dispatch(
            updateMedicalReportSettings(res.data.medical_report_settings),
          );
        }
        if (res?.data?.prescription_settings) {
          dispatch(updatePrescriptionSettings(res.data.prescription_settings));
        }
        if (res?.data?.discount_settings)
          dispatch(updateDiscountSettings(res.data.discount_settings));
        if (res?.data?.paybill_settings)
          dispatch(updatePayBillSettings(res.data.paybill_settings));
        if (res?.data?.gl_posting_settings)
          dispatch(updateGlPostingSettings(res.data.gl_posting_settings));
        fetchSettings();
      }
    } catch (err) {
      console.error("Error updating setting:", err);
      toast.error("Failed to update setting");
    } finally {
      setUpdateLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return (
    <div className="main-page-container !h-[calc(100vh-50px)] !overflow-y-auto scrollbar">
      <div className="w-full h-full mt-2 flex flex-col lg:flex-row gap-4 min-h-0 items-stretch">
        <aside className="w-full lg:w-72 shrink-0 self-stretch bg-white border border-gray-200 rounded-lg p-3 h-full overflow-y-auto">
          <div className="flex items-center gap-2 px-2 pb-3 mb-2 border-b border-gray-200">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center justify-center rounded-md p-2 text-gray-600 hover:bg-gray-100"
              aria-label={t("Back")}
            >
              {i18n.language === "en" ? (
                <FaArrowCircleLeft size={20} />
              ) : (
                <FaArrowCircleRight size={20} />
              )}
            </button>
            <div className="text-lg font-bold text-gray-800">
              {t("Settings")}
            </div>
          </div>
          {generalSections.map(({ label, icon: Icon }, index) => (
            <button
              key={label}
              type="button"
              onClick={() => {
                setActiveSection("general");
                setGeneralSectionIndex(index);
              }}
              className={`w-full flex items-center gap-3 rounded-md px-3 py-3 text-left ${activeSection === "general" && generalSectionIndex === index ? "bg-primary/10 text-primary font-semibold" : "text-gray-600 hover:bg-gray-100"}`}
            >
              <Icon size={17} />
              <span>{t(label)}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => setActiveSection("medical-report")}
            className={`w-full flex items-center gap-3 rounded-md px-3 py-3 text-left ${activeSection === "medical-report" ? "bg-primary/10 text-primary font-semibold" : "text-gray-600 hover:bg-gray-100"}`}
          >
            <FaBriefcaseMedical size={17} />
            <span>{t("Medical Summary Settings")}</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveSection("prescription")}
            className={`w-full flex items-center gap-3 rounded-md px-3 py-3 text-left ${activeSection === "prescription" ? "bg-primary/10 text-primary font-semibold" : "text-gray-600 hover:bg-gray-100"}`}
          >
            <FaCapsules size={17} />
            <span>{t("Prescription Settings")}</span>
          </button>
        </aside>
        <main className="flex-1 min-w-0 overflow-y-auto">
          {activeSection === "medical-report" ? (
            <MedicalReportSettings embedded />
          ) : activeSection === "prescription" ? (
            <PrescriptionSettings
              embedded
              settings={settings}
              updateSettings={updateSettings}
              updateLoading={updateLoading}
            />
          ) : (
            <GeneralSettings
              settings={settings}
              setSettings={setSettings}
              updateSettings={updateSettings}
              loading={loading}
              setLoading={setLoading}
              updateLoading={updateLoading}
              onSettingsChange={handleSettingsChange}
              activeSectionIndex={generalSectionIndex}
            />
          )}
        </main>
      </div>
    </div>
  );
}

export default Settings;
